import IPlayerNote from "../../models/scorer/IPlayerNote";
import {INoteScoreCriteria, IScoredNote} from "../../models/scorer/scorerTypes";
import Measure from "../../models/view/lessonPage/Measure";
import NotePath from "../../models/soundslice/NotePath";
import {NoteEventTime} from "@playground-sessions/pitch-detection/types/toMidi";
import Note from "../../models/soundslice/Note";
import {ILogArguments} from "../log/ILogArguments";
import {midiNumberToNoteName} from "../lesson/soundUtilities";

/**
 * -----Terminology-----
 * Note score: A score given to a note based on how well the user played it. Possible assessments are 'correct', 'incorrect', and 'close'.
 * PlayerNote: A note played by the user. Note can come from a physical piano keyboard or through the virtual piano keyboard.
 */

/**
 * Workers have a couple limitations that I have run into. They can't seem to new up classes defined in different files (could be a React thing).
 * They also don't seem to be able to hoist classes defined in this file. So class definitions have to come first, then
 * the usage of the classes will be toward the end of this file after the class definitions.
 */

/**
 * This function contains the logic for scoring notes in a music lesson application. It calculates the correct and
 * incorrect thresholds for note timing, scores notes based on these thresholds, and handles messages from the main
 * thread related to note playback.
 *
 * Here's a simplified overview of what the worker function does:
 * 1) It receives messages from the main thread, which include commands like 'beginScoring', 'handlePlayerNotePlayed',
 * 'handlePlayerNoteReleased', and 'stopScoringAndGetScore'.
 * 2) Depending on the command received, it performs different tasks. For example, if it receives 'beginScoring', it
 * starts the process of scoring notes. If it receives 'handlePlayerNotePlayed', it handles the event of a note being
 * played by the user.
 * 3) It calculates the correct and incorrect thresholds for note timing based on the current tempo and difficulty
 * level of the lesson.
 * 4) It scores notes based on these thresholds and the timing of when the user played the notes.
 * 5) It sends messages back to the main thread with the results of the scoring.
 *
 */
export default function workerFunction() {
  interface IState {
    unscoredNotesCriteriaInSelection: INoteScoreCriteria[];
    scoredNotes: IScoredNote[];//all notes in segment and
    unscoredPlayerNotes: IPlayerNote[];
    currentlyPressedMidiNotes: IPlayerNote[]; //used for scoring ties. when the tie end is encountered, we check this array to see if it's currently pressed.
    scoringStartTime: number | undefined; //performance.now() player notes will have start times relative to this.  If undefined, no scoring is taking place.
    speed: number; // tempo modifier to speedup/slowdown playback. Used only to modify the tempo.
    speedRelativeToRecordingIndex: number; // the notes passed in will have offsets and durations relative to either normal or slow backing track. We therefore need the speed relative to the slow or normal backing track.
    //in some cases, like when there is a sixteenth note at the end of the song, we need a little extra time to finish scoring so that the last note is marked as incorrect. e.g. the incorrectThreshold could be after the song has ended.  This was a bug in the legacy app that no one seemed to notice, but we'll fix it anyway.
    //use this ref to do a setTimeout when endScoringAndGetScore is called.
    noteScoreIntervalMs: number;
    correctThresholdMs: number;
    correctMsFloor: number; // since our thresholds are calculated based on difficulty, speed, etc, we need a lowest allowable threshold for correct note, or it'll be impossible in some scenarios to get the note right.
    correctBeatDivision: number; // modifier multiplied against the duration a single beat takes, in order to calculate the threshold.
    incorrectThresholdMs: number;
    incorrectMsFloor: number; // since our thresholds are calculated based on difficulty, speed, etc, we need a lowest allowable threshold for incorrect note.
    incorrectBeatDivision: number; // modifier multiplied against the duration of a single beat takes, in order to calculate the threshold.
    seekOffsetSeconds: number; // where the cursor is. i.e. where playback is began from.
    loopEndOffsetSeconds: number; // if there is a loop, we don't want to bother with notes after the loop ends.
    scoringIntervalId: any | undefined;
    lessonDifficulty: number;
    measures: Measure[];
    speedAdjustedBpm: number;
    isInitialized: boolean;
    state: "new" | "scoring" | "stopped";
    noteScoreIntervalMsParam: number; // how often to score
    isPitchDetectionEnabled: boolean;
    isPitchDetectionDebuggingEnabled: boolean;
  }

  const state: IState = {
    unscoredNotesCriteriaInSelection: [],
    scoredNotes: [],
    unscoredPlayerNotes: [],
    currentlyPressedMidiNotes: [],
    scoringStartTime: undefined,
    speed: 0,
    speedRelativeToRecordingIndex: 0,
    noteScoreIntervalMs: 0,
    correctThresholdMs: 0,
    correctMsFloor: 0,
    correctBeatDivision: 0,
    incorrectThresholdMs: 0,
    incorrectMsFloor: 0,
    incorrectBeatDivision: 0,
    seekOffsetSeconds: 0,
    loopEndOffsetSeconds: 0,
    scoringIntervalId: undefined,
    lessonDifficulty: 0,
    measures: [],
    speedAdjustedBpm: 0,
    isInitialized: false,
    state: "new",
    noteScoreIntervalMsParam: 100000,
    isPitchDetectionEnabled: false,
    isPitchDetectionDebuggingEnabled: false,
  };

  /**
   * ScoringEventHandler is responsible for handling note events and calling methods to score notes
   */
  function createScorer() {
    function initialize(
      correctMsFloor: number,
      correctBeatDivision: number,
      incorrectMsFloor: number,
      incorrectBeatDivision: number,
      speed: number,
      speedRelativeToRecordingIndex: number,
      lessonDifficulty: number,
      measures: Measure[],
      tempo: number,
      noteScoreIntervalMs: number,
      isPitchDetectionEnabled: boolean,
      isPitchDetectionDebuggingEnabled: boolean,
    ) {
      state.correctMsFloor = correctMsFloor;
      state.correctBeatDivision = correctBeatDivision;
      state.incorrectBeatDivision = incorrectBeatDivision;
      state.incorrectMsFloor = incorrectMsFloor;
      state.speed = speed;
      state.speedRelativeToRecordingIndex = speedRelativeToRecordingIndex;
      state.lessonDifficulty = lessonDifficulty;
      state.measures = measures;
      state.speedAdjustedBpm = tempo * speed;
      state.noteScoreIntervalMs = noteScoreIntervalMs;
      state.isPitchDetectionEnabled = isPitchDetectionEnabled;
      state.isPitchDetectionDebuggingEnabled = isPitchDetectionDebuggingEnabled;

      setThreshholds();

      emitLog({
        message: "initialized - state",
        data: [
          { key: "correctMsFloor", value: state.correctMsFloor },
          { key: "correctBeatDivision", value: state.correctBeatDivision },
          { key: "incorrectBeatDivision", value: state.incorrectBeatDivision },
          { key: "incorrectMsFloor", value: state.incorrectMsFloor },
          { key: "speed", value: state.speed },
          { key: "speedRelativeToRecordingIndex", value: state.speedRelativeToRecordingIndex },
          { key: "lessonDifficulty", value: state.lessonDifficulty },
          { key: "measures", value: state.measures },
          { key: "speedAdjustedBpm", value: state.speedAdjustedBpm },
          { key: "noteScoreIntervalMs", value: state.noteScoreIntervalMs },
          { key: "isPitchDetectionEnabled", value: state.isPitchDetectionEnabled },
          { key: "isPitchDetectionDebuggingEnabled", value: state.isPitchDetectionDebuggingEnabled },
        ]
      });

      state.isInitialized = true;
    }

    function setThreshholds(): void {
      const correctAndIncorrectThresholds = calculateCorrectAndIncorrectThresholds(
        state.speedAdjustedBpm,
        state.lessonDifficulty,
        state.correctMsFloor,
        state.correctBeatDivision,
        state.incorrectMsFloor,
        state.incorrectBeatDivision,
      );

      state.correctThresholdMs = correctAndIncorrectThresholds.correctThreshold;
      state.incorrectThresholdMs = correctAndIncorrectThresholds.incorrectThreshold; //store this value outside of scope so we can wait this amount + score interval at the end of song to ensure scoring has completed.
    }

    function getIsInitialized(): boolean {
      return state.isInitialized;
    }

    function getCorrectThresholdMs(): number {
      ensureInitialized();
      return state.correctThresholdMs;
    }

    function ensureInitialized() {
      if (!state.isInitialized) {
        throw new Error('NoteScorer not initialized');
      }
    }

    function getMeasures(): Measure[] {
      ensureInitialized();
      return state.measures;
    }

    function onNoteEventReceived(e: MessageEvent): void {
      const data = e.data;
      const method = data.method;
      switch(method) {
        case `handlePlayerNotePlayed`:
          onHandlePlayerNotePlayedMessageReceived(data.midi, data.velocity, data.source, data.id, data.receivedTime, data.performanceNowOffset);
          break;
        case 'initialize':
          onInitializeScorerMessageReceived(data);
          break;
        case `beginScoring`:
          onBeginScoringMessageReceived(data);
          break;
        case `stopScoringAndGetScore`:
          onStopScoringAndGetScoreMessageReceived(data.requestId, data.waitForScoringToComplete);
          break;
        case `handlePlayerNoteReleased`:
          onHandlePlayerNoteReleasedMessageReceived(data.midi);
          break;
        case 'notesDetected':
          onNotesDetectedMessageReceived(data.notes);
          break;
        case 'isPitchDetectionEnabled_changed':
          onIsPitchDetectionEnabledChanged(data.isPitchDetectionEnabled);
      }
    }

    function onIsPitchDetectionEnabledChanged(isPitchDetectionEnabled: boolean) {
      state.isPitchDetectionEnabled = isPitchDetectionEnabled;
    }

    function onInitializeScorerMessageReceived(data: any) {
      initialize(
        data.correctMsFloor,
        data.correctBeatDivision,
        data.incorrectMsFloor,
        data.incorrectBeatDivision,
        data.speed,
        data.speedRelativeToRecordingIndex,
        data.lessonDifficulty,
        data.measures,
        data.tempo,
        data.noteScoreIntervalMs,
        data.isPitchDetectionEnabled,
        data.isPitchDetectionDebuggingEnabled,
      );
    }

    function onBeginScoringMessageReceived(data: any) {
      beginScoring(
        data.notesInSegment,
        data.seekOffsetSeconds,
        data.loopEndOffsetSeconds,
      );
    }

    /**
     * Each time a player plays a note while scoring is happening, we add it to an array of notes to be scored during
     * the next predetermined scoring interval. We also add it to the currently being pressed notes array so we can score
     * tie end notes.
     */
    function onHandlePlayerNotePlayedMessageReceived(midi: number, velocity: number, source: string, id: string, receivedTime: number, performanceNowOffset: number) {
      const playerNote = getPlayerNoteFromNotePlayedMessage(midi, id, performanceNowOffset);

      state.currentlyPressedMidiNotes.push(playerNote);

      if(state.scoringStartTime) {
        state.unscoredPlayerNotes.push(playerNote);
      }
    }

    function getPlayerNoteFromNotePlayedMessage(midi: number, id: string, performanceNowOffset: number): IPlayerNote
    {
      const now = performance.now();
      const playStartTimeMs = state.scoringStartTime ?  now - state.scoringStartTime - performanceNowOffset : 0;
      const playerNote: IPlayerNote = {id, midi, playStartTimeMs, createdAtInMS: (new Date()).getTime()};
      return playerNote;
    }

    /**
     * Remove the released note from the currentlyPressedMidiNotes.
     */
    function onHandlePlayerNoteReleasedMessageReceived(midi: number,) {
      const index = state.currentlyPressedMidiNotes.findIndex((c: { midi: number; }) => c.midi === midi);
      if(index >= 0) {
        state.currentlyPressedMidiNotes.splice(index, 1);
      }
    }

    /*
    Clears out everything in the currentlyPressedMidiNotes array except for notes that were played within the last 1/2 second.
    There seems to be a bug somewhere in our software where we are recording note(s) that are pressed, but somehow we're not recording
    when they are released. This is a workaround for that bug. This method should be called right before we start scoring. We leave
    the notes played within the last second so we can pick up notes played right at the end of the count-in.
     */
    function clearCurrentlyPressedMidiNotesExceptForRecentNotes() {
      const now = (new Date()).getTime();
      const fudgeFactor = 250;
      const timeCutoff = now - fudgeFactor;

      for(let i = state.currentlyPressedMidiNotes.length - 1; i >= 0; --i) {
        const note = state.currentlyPressedMidiNotes[i];
        if(note.createdAtInMS < timeCutoff) {
          state.currentlyPressedMidiNotes.splice(i, 1);
        }
      }
    }

    /**
     * NoteEventTime format
     * -amplitude: float
     * -durationSeconds: float
     * -pitchBends: number[]
     * -pitchMidi: int
     * -startTimeSeconds: float
     */
    function onNotesDetectedMessageReceived(detectedNotes: NoteEventTime[]) {
      if (getIsInitialized()) {
        // This may cause "JSON.stringify cannot serialize cyclic structures." Re-enable after resolving that error.
        // emitLog({ message: "DetectedNotes", data: [{ key: "detectedNotes", value: JSON.stringify(detectedNotes) }] });

        const unscoredPlayerNotes = convertDetectedNotesToPlayerNotes(detectedNotes);
        const unscoredPlayerNotesCopy = deepCopyArray(unscoredPlayerNotes);

        // create an array of unscoredPlayerNotes containing midi and playStartTimeMs values.
        const dumpStuff = detectedNotes.map(note => {
          return {
            midi: midiNumberToNoteName(note.pitchMidi),
            playStartTimeMs: note.startTimeSeconds * 1000 // convert seconds to milliseconds
          };
        });

        // Sort dumpStuff by playStartTimeMs
        dumpStuff.sort((a, b) => a.playStartTimeMs - b.playStartTimeMs);

        // This may cause "JSON.stringify cannot serialize cyclic structures." Re-enable after resolving that error.
        // emitLog({ message: "DumpStuff", data: [{ key: "dumpStuff", value: JSON.stringify(dumpStuff) }] });

        const intervalScoredNotes = scoreNotesInInterval(
          0,
          state.speed,
          state.unscoredNotesCriteriaInSelection,
          state.currentlyPressedMidiNotes,
          unscoredPlayerNotesCopy,
          getCorrectThresholdMs(),
          state.incorrectThresholdMs,
          getMeasures(),
          state.seekOffsetSeconds,
          true
        );

        // This may cause "JSON.stringify cannot serialize cyclic structures." Re-enable after resolving that error.
        // emitLog({ message: "Scored notes", data: [{ key: "intervalScoredNotes", value: JSON.stringify(intervalScoredNotes) }] });

        // Get rid of all the notes that have a noteScoreReason of incorrect. We don't want to include those for pitch detection scoring.
        if (state.isPitchDetectionEnabled && !state.isPitchDetectionDebuggingEnabled) {
          emitLog({ message: "Filtering out incorrect notes" });
          for (let i = 0; i < intervalScoredNotes.length; i++) {
            if (intervalScoredNotes[i].noteScoreReason === 1) {
              intervalScoredNotes.splice(i, 1);
              i--;
            }
          }
        }

        // This may cause "JSON.stringify cannot serialize cyclic structures." Re-enable after resolving that error.
        // emitLog({ message: "Scored notes after filtering: ", data: [{ key: "intervalScoredNotes", value: JSON.stringify(intervalScoredNotes) }] });

        // Note: intervalScoredNotes is an array that is infinitely recursable. Note -> noteScoreCriteria -> scoredNote -> noteScoreCriteria -> scoredNote -> ...
        state.scoredNotes = intervalScoredNotes;
        emitScoredNotes_completed(intervalScoredNotes);
        emitDetectedNotesScoring_completed();
      }
    }

    function deepCopyArray<Type>(inputValue: Type[]): Type[] {
      // @ts-ignore
      return inputValue.map(item => {
        if (Array.isArray(item)) {
          return deepCopyArray(item); // Recursive call for nested arrays
        } else if (item && typeof item === 'object') {
          return deepCopyObject(item); // Recursive call for objects
        }
        return item;
      });
    }

    function deepCopyObject<T>(obj: T): T {
      const copiedObj = {} as T;
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];
          if (Array.isArray(value)) {
            // @ts-ignore
            copiedObj[key] = deepCopyArray(value);
          } else if (value && typeof value === 'object') {
            copiedObj[key] = deepCopyObject(value);
          } else {
            copiedObj[key] = value;
          }
        }
      }
      return copiedObj;
    }

    function convertDetectedNotesToPlayerNotes(notes: NoteEventTime[]): IPlayerNote[] {
      const result = notes.map((note) => {
        return {
          id: createGuid("detected"),
          midi: note.pitchMidi,
          playStartTimeMs: note.startTimeSeconds * 1000,
          createdAtInMS: (new Date()).getTime(),
        };
      });

      return result;
    }

    function createGuid(prefix?: string): string {
      function create4CharacterString() {
        return Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);
      }
      let result = create4CharacterString() + create4CharacterString()
        + '-' + create4CharacterString()
        + '-' + '4' + create4CharacterString().substr(0, 3)
        + '-' + create4CharacterString()
        + '-' + create4CharacterString() + create4CharacterString() + create4CharacterString();

      if (prefix) {
        result = prefix + '-' + result;
      }

      return result;
    }

    function emitScoredNotes_completed(scoredNotes: IScoredNote[]) {
      // Only post a message if there are notes that have been scored.
      if (scoredNotes.length > 0) {
        emitLog({ message: "Posting scored notes", data: [{ key: "scoreNotesLength", value: scoredNotes.length }] });
        self.postMessage({method: 'scoreInterval_completed', scoredNotes});
      }
    }

    function emitDetectedNotesScoring_completed(): void {
      self.postMessage({method: 'detectedNotesScoring_completed'});
    }

    function emitScoreMessage(requestId: string, scoringEndTime: number): void {
      self.postMessage({
        requestId,
        method: 'score',
        scoredNotes: state.scoredNotes,
        unscoredNotesCriteriaInSelection: state.unscoredNotesCriteriaInSelection,
        unscoredPlayerNotes: state.unscoredPlayerNotes,
        speed: state.speed,
        scoringStartTime: state.scoringStartTime,
        scoringEndTime,
        loopEndOffsetSeconds: state.loopEndOffsetSeconds,
        seekOffsetSeconds: state.seekOffsetSeconds,
      });
    }

    /**
     * Starts an interval to run note score evaluation for notes in the music and notes the user plays.
     * @param notesInSegment - array of notes to score. can be a subset of notes in the segment, when there is looping, etc.
     * @param seekOffsetSecondsParam - where the cursor is. i.e. where playback is began from.
     * @param loopEndOffsetSecondsParam - if there is a loop, we don't want to bother with notes after the loop ends.
     */
    function beginScoring(
      notesInSegment: Note[],
      seekOffsetSecondsParam: number,
      loopEndOffsetSecondsParam: number,
    ) {
      state.seekOffsetSeconds = seekOffsetSecondsParam;
      state.loopEndOffsetSeconds = loopEndOffsetSecondsParam;

      clearCurrentlyPressedMidiNotesExceptForRecentNotes();
      clearScoringInterval();

      //keep track of when we started so we can score notes that aren't played in time.
      state.scoringStartTime = performance.now();

      //We don't want to bother scoring notes that don't need scoring, so filter out notes that start before the seekOffsetSeconds or after the loopEndOffsetSeconds, and map them into INoteCriteria objects.
      const scorableNotesInSegment = getNotesBetweenOffsets(notesInSegment, state.seekOffsetSeconds, state.loopEndOffsetSeconds);
      state.unscoredNotesCriteriaInSelection = createNotesScoreCriteria(scorableNotesInSegment, state.speedRelativeToRecordingIndex, state.seekOffsetSeconds, state.measures);

      //getting the first notes correct is challenging due to notes not getting added to player notes if they are played before scoring starts. i.e. you can't be early, so your window of getting correct is smaller.
      //as a fix, create player notes for all keys being held down, and set their playStartTimeMs to 0.
      //this means that if you hold down the correct notes 60 seconds before starting, the notes will still be marked as correct.
      state.unscoredPlayerNotes = state.unscoredPlayerNotes.concat(state.currentlyPressedMidiNotes);

      emitLog({
        message: "Scoring started",
        data: [
          { key: "isPitchDetectionEnabled", value: state.isPitchDetectionEnabled },
          { key: "isPitchDetectionDebuggingEnabled", value: state.isPitchDetectionDebuggingEnabled },
        ]
      });
      if (!state.isPitchDetectionEnabled) {
        createScoringInterval();
      }
    }

    function createScoringInterval() {
      state.scoringIntervalId = setInterval(
        ()=> {
          if(state.scoringStartTime === undefined) {
            return;
          }

          const intervalScoredNotes = scoreNotesInInterval(
            state.scoringStartTime,
            state.speed,
            state.unscoredNotesCriteriaInSelection,
            state.currentlyPressedMidiNotes,
            state.unscoredPlayerNotes,
            state.correctThresholdMs,
            state.incorrectThresholdMs,
            state.measures,
            state.seekOffsetSeconds,
            false
          );

          state.scoredNotes = state.scoredNotes.concat(intervalScoredNotes);

          emitScoredNotes_completed(intervalScoredNotes);
        },
        state.noteScoreIntervalMs
      );
    }

    // this is needed so we can stop the interval when cancelScoring is called.
    function clearScoringInterval(): void  {
      if (state.scoringIntervalId === undefined) {
        return;
      }
      clearInterval(state.scoringIntervalId);
      state.scoringIntervalId = undefined;
    }

    function resetScoringData() {
      emitLog({ message: "Resetting scoring data" });
      state.scoredNotes = [];
      state.unscoredPlayerNotes = [];
      state.scoringStartTime = undefined; //indicate that we've stopped scoring.
      state.speed = -1;
      state.state = "new";
    }

    function onStopScoringAndGetScoreMessageReceived(requestId: string, waitForScoringToComplete: boolean) {
      emitLog({ message: "onStopScoringAndGetScoreMessageReceived" });
      //in most cases we want to wait a couple of intervals so that scoring can complete.
      //this function is called when startScoring is called though, so in that case, we don't want to wait for scoring to complete, we just want to clean up any scoring data.
      const intervalsLeftBeforeScoringIsCompleted =  Math.ceil(state.incorrectThresholdMs / state.noteScoreIntervalMs); //e.g. incorrectThreshold = 100, noteScoreInterval = 50, then wait 2 intervals before complete.
      const msToWaitForScoringToFinish = state.noteScoreIntervalMs * intervalsLeftBeforeScoringIsCompleted + 10;

      const callback = () => {
        clearScoringInterval(); //stop evaluating notes

        const scoringEndTime = performance.now();

        emitScoreMessage(requestId, scoringEndTime);

        resetScoringData();
      }

      if(waitForScoringToComplete) {
        setTimeout(callback, msToWaitForScoringToFinish);
      }else{
        callback();
      }
    }

    return {
      onNoteEventReceived,
    };
  } // End createScorer

  createScorer.bind(state);

  enum NoteEvaluationEnum { correct, incorrect, close,}
  enum NoteTypeEnum { whole, half, quarter, lessThanQuarter,}
  enum NoteScoreReason {
    IncorrectBecauseItWasNotPlayed,
    IncorrectBecauseItWasExtraneous,
    CorrectBecauseItWasPlayedWithinThreshold,
    CloseBecauseItWasTheRightPitchButWrongTime,
  }

  //@ts-ignore
  const self = this || {};
  const scorer = createScorer();

  /**
   * Routes messages from the main thread (noteEvaluationService.ts)
   */
  self.onmessage = function(e: MessageEvent) {
    scorer.onNoteEventReceived(e);
  };

  function getNotesBetweenOffsets(notes: Note[], seekOffsetSeconds: number, loopEndOffsetSeconds: number) {
    loopEndOffsetSeconds = loopEndOffsetSeconds < 0
      ? Number.POSITIVE_INFINITY
      : loopEndOffsetSeconds;
    return notes.filter(n => n.offset >= seekOffsetSeconds && n.offset < loopEndOffsetSeconds);
  }

  /**
   * Find and score notes that either should have been played by now, or were extraneously played by the user.
   * Modifies the unscoredNotesCriteriaInSelection and unscoredPlayerNotes arrays as notes are scored.
   * Scoring is done in 3 phases:
   * 1 - Player notes are scored by finding corresponding notes in unscoredNotesCriteriaInSelection and seeing if they
   *     were played in the correct/incorrect/close thresholds. Notes not found are scored as incorrect/extraneous.
   * 2 - Tie end notes are scored by seeing if the user is holding down the key when the tie end note begins.
   * 3 - Unplayed notes are scored by finding what notes weren't played by the user.
   * @param scoringStartTimeMs - when scoring began
   * @param speed - speed modifier for playback.
   * @param unscoredNotesCriteriaInSelection - notes from the piece of music of the segment which have not been scored.
   * @param currentlyPressedMidiNotes - array of midi numbers indicating which notes are currently being played by the
   *    user.  This is used for scoring tie end notes.
   * @param unscoredPlayerNotes - notes which the player has played which have not been scored.
   * @param correctThresholdMs - window of time where a note played by the player is deemed correct. e.g. segment note
   *    offset of 1000 ms could have a correct time offset of 300ms, so the player could play between 700ms and 1300ms and
   *    the note would be correct.
   * @param incorrectThresholdMs - window of time where a note played by the player is deemed incorrect. e.g. segment
   *    note offset of 1000 ms could have a absent time offset of 600ms, so if the player played the note
   */
  function scoreNotesInInterval(
    scoringStartTimeMs: number, // When
    speed: number,
    unscoredNotesCriteriaInSelection: INoteScoreCriteria[],
    currentlyPressedMidiNotes: IPlayerNote[],
    unscoredPlayerNotes: IPlayerNote[],
    correctThresholdMs: number,
    incorrectThresholdMs: number,
    measuresInSegment: Measure[],
    seekOffsetSeconds: number,
    isPitchDetectionEnabled: boolean,
  ) {
    let scoredNotes: IScoredNote[] = [];

    let scoredPlayerNotes: IScoredNote[] = [];

    if (isPitchDetectionEnabled) {
      emitLog({ message: "Scoring with pitch detection" });
      scoredPlayerNotes = scorePlayerNotesForPitchDetection(
        unscoredNotesCriteriaInSelection,
        unscoredPlayerNotes,
        correctThresholdMs,
        incorrectThresholdMs,
        measuresInSegment,
        seekOffsetSeconds,
        speed
      );
    } else {
      scoredPlayerNotes = scorePlayerNotes(
        unscoredNotesCriteriaInSelection,
        unscoredPlayerNotes,
        correctThresholdMs,
        incorrectThresholdMs,
        measuresInSegment,
        seekOffsetSeconds,
        speed
      );
    }

    removeScoredNotesFromUnscoredNotesCriteria(scoredPlayerNotes, unscoredNotesCriteriaInSelection);
    removeScoredNotesFromUnscoredPlayerNotes(scoredPlayerNotes, unscoredPlayerNotes);
    scoredNotes = scoredNotes.concat(scoredPlayerNotes);

    //ensure that the player is holding down the note when tie end is encountered.
    const scoredTieEndNotes = scoreUnscoredTieEndNotes(unscoredNotesCriteriaInSelection, currentlyPressedMidiNotes, scoringStartTimeMs, isPitchDetectionEnabled);
    removeScoredNotesFromUnscoredNotesCriteria(scoredTieEndNotes, unscoredNotesCriteriaInSelection);
    scoredNotes = scoredNotes.concat(scoredTieEndNotes);

    //score the notes that haven't been played in time
    const scoredUnplayedNotesCriteria = scoreUnscoredNotesCriteriaThatWerentPlayedInTime(
      unscoredNotesCriteriaInSelection,
      incorrectThresholdMs,
      scoringStartTimeMs
    );

    removeScoredNotesFromUnscoredNotesCriteria(scoredUnplayedNotesCriteria, unscoredNotesCriteriaInSelection);
    scoredNotes = scoredNotes.concat(scoredUnplayedNotesCriteria);

    return scoredNotes;
  }

  /**
   * Iterate over the unscored notes and score the tie end notes.
   */
  function scoreUnscoredTieEndNotes(unscoredNotesCriteriaInSelection: INoteScoreCriteria[], currentlyPressedMidiNotes: IPlayerNote[], scoringStartTimeMs: number, isPitchDetectionEnabled: boolean) {
    const timeSinceScoringStartedMs = performance.now() - scoringStartTimeMs;
    const scoredNotes: IScoredNote[] = [];

    for(let i = 0; i < unscoredNotesCriteriaInSelection.length; ++i) {
      const unscoredNote = unscoredNotesCriteriaInSelection[i];

      if(!unscoredNote.isTieEnd) {
        continue;
      }

      const scoredNote = scoreUnscoredTieEndNote(unscoredNote, currentlyPressedMidiNotes, timeSinceScoringStartedMs, isPitchDetectionEnabled);

      if(scoredNote !== undefined) {
        unscoredNote.scoredNote = scoredNote; //other tie end notes in the chain need to know their previous note's note score reason.
        scoredNotes.push(scoredNote);
      }
    }

    return scoredNotes;
  }

  /**
   * We score tie notes by keeping track of which notes the user is currently pressing, and as we encounter the beginning of a tie note, we check and see if the user is currently pressing it.
   * If so, the note is marked correct.  Otherwise it is marked incorrect.
   */
  function scoreUnscoredTieEndNote(unscoredTieEndNote: INoteScoreCriteria, currentlyPressedMidiNotes: IPlayerNote[], timeSinceScoringStartedMs: number, isPitchDetectionEnabled: boolean) : IScoredNote | undefined{
    //determine if the tieEnd should be pressed at this moment in time
    if(timeSinceScoringStartedMs < unscoredTieEndNote.startRelativeToSpeedAndSeekOffsetMs) { return; }

    //initially give the tie the same score reason as the previous note had.  If not found (should be always) mark it incorrect.
    let noteScoreReason = unscoredTieEndNote.previousTieNote?.scoredNote?.noteScoreReason? unscoredTieEndNote.previousTieNote.scoredNote.noteScoreReason : NoteScoreReason.IncorrectBecauseItWasNotPlayed;
    if(!unscoredTieEndNote.previousTieNote) {
      // This may cause "JSON.stringify cannot serialize cyclic structures." Re-enable after resolving that error.
      // emitLog({ message: "No previous tie note to evaluate, marking incorrect", data: [{ key: "unscoredTieEndNote", value: JSON.stringify(unscoredTieEndNote) }] });
    }

    let score = null;

    const currentlyPressedPlayerNote = currentlyPressedMidiNotes.find(c => c.midi === unscoredTieEndNote.midi);

    // If pitch detection is turned on the tied notes should simply get whatever score the first note in the tie got. So only run this code if pitch detection is off.
    if (!isPitchDetectionEnabled) {
      //if the tie note isn't being held down, mark it incorrect.
      if (!currentlyPressedPlayerNote) {
        noteScoreReason = NoteScoreReason.IncorrectBecauseItWasNotPlayed;
      }

      //if the user breaks the tie, then presses the same note to continue the tie, we need to mark it as incorrect.
      if (currentlyPressedPlayerNote?.id !== unscoredTieEndNote.previousTieNote?.scoredNote?.playerNoteId) {
        noteScoreReason = NoteScoreReason.IncorrectBecauseItWasExtraneous;
      }
    }

    score = noteScoreReason === NoteScoreReason.CorrectBecauseItWasPlayedWithinThreshold ? NoteEvaluationEnum.correct : NoteEvaluationEnum.incorrect;

    return {
      score,
      playerMidi: unscoredTieEndNote.midi,
      noteId: unscoredTieEndNote.noteId,
      noteScoreCriteria: unscoredTieEndNote,
      noteScoreReason,
      barIndex: unscoredTieEndNote.measureIndex,
      playerNoteId: currentlyPressedPlayerNote?.id
    };
  }

  /**
   * Modify the passed in array of unscoredNotesCriteriaInSelection by removing each scored note.
   */
  function removeScoredNotesFromUnscoredNotesCriteria(scoredNotes: IScoredNote[], unscoredNotesCriteriaInSelection: INoteScoreCriteria[]) {
    for(let i = 0; i < scoredNotes.length; ++i) {
      const scoredNote = scoredNotes[i];
      const index = unscoredNotesCriteriaInSelection.findIndex((u)=> u.noteId == scoredNote.noteId);
      if(index >= 0) {
        unscoredNotesCriteriaInSelection.splice(index, 1);
      }
    }
  }

  /**
   * Modify the passed in array of unscoredPlayerNotes by removing notes that have been scored.
   */
  function removeScoredNotesFromUnscoredPlayerNotes(scoredNotes: IScoredNote[], unscoredPlayerNotes: IPlayerNote[]) {
    for(let i = 0; i < scoredNotes.length; ++i) {
      const scoredNote = scoredNotes[i];
      const index = unscoredPlayerNotes.findIndex((u)=> u.id === scoredNote.playerNoteId);
      if(index >= 0) {
        unscoredPlayerNotes.splice(index, 1);
      }
    }
  }

  /**
   * Iterate over each unscored player note and score it by finding it's associated unscoredNotesCriteriaInSelection
   * @param unscoredNotesCriteria - array of note criteria which hasn't been scored yet.  when a player note is associated with an entry in this array, it is removed so that we don't attempt to score it more than once.
   * @param playerNotes - array of player notes that have been collected since the last interval.
   * @param correctThresholdMs - window of time allowed in order for the note to be marked correct.
   * @param incorrectThresholdMs - window of time allowed before the note is marked incorrect.
   * @param measuresInSegment - in order to calculate the bar, and percentage into bar an extraneous note should be placed at (see drawTempNote SS api), we need to know the duration of each measure.
   * @param seekOffsetSeconds - where playback started at.
   * @param speed - tempo modifier
   */
  function scorePlayerNotes(
    unscoredNotesCriteria: INoteScoreCriteria[],
    playerNotes: IPlayerNote[],
    correctThresholdMs: number,
    incorrectThresholdMs: number,
    measuresInSegment: Measure[],
    seekOffsetSeconds: number,
    speed: number,
  ) {
    //the user could have hit the same note X times within an interval, so we need to remove the notes as they are scored, so we don't end up scoring the same note more than once.
    const unscoredNotesCriteriaWorkingCopy = unscoredNotesCriteria.slice(); //we need to remove notes as we find matches and evaluate, but don't want to alter the original array.
    const scoredNotes: IScoredNote[] = [];

    playerNotes.forEach(playerNote => {
      //try to find the corresponding note from the segment notes
      const matchingExpectedNote =
        findMatchingExpectedNoteThatIsClosestInTimeAndThatDoesntExceedTheIncorrectThreshold(
          playerNote.midi,
          playerNote.playStartTimeMs,
          unscoredNotesCriteriaWorkingCopy,
          incorrectThresholdMs
        );

      const scoredNote = scorePlayerNote(
        playerNote,
        matchingExpectedNote,
        correctThresholdMs,
        incorrectThresholdMs,
        measuresInSegment,
        seekOffsetSeconds,
        speed
      );

      scoredNotes.push(scoredNote);

      // DH - 6/12/2024: I'm sure adding the scoredNotes to the noteScoreCriteria is needed for something, but it
      // also creates an infinite loop: scoredNote -> noteScoreCriteria -> scoredNote -> noteScoreCriteria -> ...
      if(scoredNote.noteScoreCriteria) {
        scoredNote.noteScoreCriteria.scoredNote = scoredNote;
      }

      //if the scored player note has a match in the unscored note criteria, remove it so we don't score it again.
      removeScoredNotesFromUnscoredNotesCriteria([scoredNote], unscoredNotesCriteriaWorkingCopy);
    });
    return scoredNotes;
  }

  function scorePlayerNotesForPitchDetection(
    unscoredNotesCriteria: INoteScoreCriteria[],
    playerNotes: IPlayerNote[],
    correctThresholdMs: number,
    incorrectThresholdMs: number,
    measuresInSegment: Measure[],
    seekOffsetSeconds: number,
    speed: number,
  ) {
    const scoredNotes: IScoredNote[] = [];

    unscoredNotesCriteria.forEach(expectedNote => {
      // Find out if there are of notes expected at the same time as the current expectedNote that are 1 octave above or below the expectedNote
      const otherExpectedNotesPlusOrMinusOneOctave = unscoredNotesCriteria.filter(otherExpectedNote => {
        return Math.abs(otherExpectedNote.startRelativeToSpeedAndSeekOffsetMs - expectedNote.startRelativeToSpeedAndSeekOffsetMs) == 0 && otherExpectedNote.noteId != expectedNote.noteId;
      });

      const canGetCreditForNoteInWrongOctave = false; //otherExpectedNotesPlusOrMinusOneOctave.length == 0;

      const matchingPlayerNote = findMatchingPlayedNoteThatIsClosestInTimeAndThatDoesntExceedTheIncorrectThreshold(
        expectedNote.midi,
        expectedNote.startRelativeToSpeedAndSeekOffsetMs,
        playerNotes,
        incorrectThresholdMs,
        canGetCreditForNoteInWrongOctave,
        measuresInSegment,
        speed,
      );

      if (matchingPlayerNote) {
        const scoredNote = scorePlayerNote(
          matchingPlayerNote,
          expectedNote,
          correctThresholdMs,
          incorrectThresholdMs,
          measuresInSegment,
          seekOffsetSeconds,
          speed
        );

        scoredNotes.push(scoredNote);

        // DH - 6/12/2024: I'm sure adding the scoredNotes to the noteScoreCriteria is needed for something, but it
        // also creates an infinite loop: scoredNote -> noteScoreCriteria -> scoredNote -> noteScoreCriteria -> ...
        if (scoredNote.noteScoreCriteria) {
          scoredNote.noteScoreCriteria.scoredNote = scoredNote;
        }

        //if the scored player note has a match in the unscored note criteria, remove it so we don't score it again.
        // removeScoredNotesFromUnscoredNotesCriteria([scoredNote], unscoredExpectedNotes);
      }
    });

    // Make a list of all playerNotes that were not matched to any expectedNote
    const unmatchedPlayerNotes = playerNotes.filter(playerNote => {
      return !scoredNotes.some(scoredNote => scoredNote.playerNoteId === playerNote.id);
    });

    // Score all unmatched player notes as extraneous
    unmatchedPlayerNotes.forEach(playerNote => {
      const scoredNote = scorePlayerNote(
        playerNote,
        undefined,
        correctThresholdMs,
        incorrectThresholdMs,
        measuresInSegment,
        seekOffsetSeconds,
        speed
      );

      scoredNotes.push(scoredNote);
    });

    return scoredNotes;
  }

  /**
   * Determine if the player note has been played correct/incorrect/close by comparing it to the matching note (if it exists) and determining if the player note was played within the correct threshold.
   * Extraneous notes (where matching midi note is undefined) will have barIndex and percentageIntoBar properties calculated, as those are needed in order to utilize the SS drawTempNote.
   * @param playerNote - midi and time the user played the note
   * @param matchingExpectedNote - if present, represents the note in the sheet music that the user played.
   * @param correctThresholdMs - window of time allowed in order for the note to be marked correct.
   * @param incorrectThresholdMs - window of time allowed before the note is marked incorrect.
   * @param measuresInSegment - in order to calculate the bar, and percentage into bar an extraneous note should be placed at (see drawTempNote SS api), we need to know the duration of each measure.
   * @param seekOffsetSeconds - where playback started
   * @param speed - tempo modifier
   */
  function scorePlayerNote(
    playerNote: IPlayerNote,
    matchingExpectedNote: INoteScoreCriteria | undefined,
    correctThresholdMs: number,
    incorrectThresholdMs: number,
    measuresInSegment: Measure[],
    seekOffsetSeconds: number,
    speed: number
  ): IScoredNote{
    let score = NoteEvaluationEnum.incorrect;
    let noteScoreReason = NoteScoreReason.IncorrectBecauseItWasExtraneous;
    let noteId;
    let barIndex = 0;
    let percentageIntoBar;

    const markNoteAsExtraneous = () => {
      //if the note is extraneous (doesnt exist in the segment) we need to tell SS which bar and what percentage into the bar to draw the temp note at
      const result = calculateBarIndexAndPercentageIntoBar(playerNote.playStartTimeMs, measuresInSegment, seekOffsetSeconds, speed);

      if(result) {
        barIndex = result.barIndex;
        percentageIntoBar = result.percentageIntoBar;
      }
    };

    if(matchingExpectedNote != undefined) {
      noteId = matchingExpectedNote.noteId;
      barIndex = matchingExpectedNote.measureIndex;
      const delta = Math.abs(matchingExpectedNote.startRelativeToSpeedAndSeekOffsetMs - playerNote.playStartTimeMs);

      if (delta <= correctThresholdMs) {
        score = NoteEvaluationEnum.correct;
        noteScoreReason = NoteScoreReason.CorrectBecauseItWasPlayedWithinThreshold;
      } else if (delta <= incorrectThresholdMs) {
        emitLog({
          message: "Close note scored",
          data: [
            { key: "expectedTime", value: matchingExpectedNote.startRelativeToSpeedAndSeekOffsetMs },
            { key: "playerTime", value: playerNote.playStartTimeMs },
            { key: "delta", value: delta }
          ]
        });
        score = NoteEvaluationEnum.close;
        noteScoreReason = NoteScoreReason.CloseBecauseItWasTheRightPitchButWrongTime;
      }
    } else {
      markNoteAsExtraneous();
    }

    if (state.isPitchDetectionDebuggingEnabled) {
      markNoteAsExtraneous();
    }

    return {
      noteId,
      playerNoteId: playerNote.id,
      score,
      playerMidi: playerNote.midi,
      barIndex,
      percentageIntoBar,
      noteScoreCriteria: matchingExpectedNote,
      noteScoreReason
    };
  }

  /**
   * Based on the midi and playStartTime of a player note, find the note from the segment that matches the midi, and is the closest in time, and which doesn't exceed the incorrect threshold.
   * @param midi - midi number of the player note
   * @param playStartTimeMs - when the player note was played
   * @param unscoredNotesCriteriaInSelection - array of notes we will search to find the closest match
   * @param incorrectThresholdMs - window of time allowed before the note is marked incorrect.
   */
  function findMatchingExpectedNoteThatIsClosestInTimeAndThatDoesntExceedTheIncorrectThreshold(
    midi: number,
    playStartTimeMs: number,
    unscoredNotesCriteriaInSelection: INoteScoreCriteria[],
    incorrectThresholdMs: number
  ): INoteScoreCriteria | undefined{
    if(unscoredNotesCriteriaInSelection.length <= 0) {
      return;
    }

    //assume the first note is the closest match.
    let closestMatchIndex = undefined;
    let closestMatchDelta = Number.MAX_SAFE_INTEGER;

    //iterate over the notes and find the nearest expected note (in time) that matches the midi number
    for(let i = 0; i < unscoredNotesCriteriaInSelection.length; ++i) {
      const note = unscoredNotesCriteriaInSelection[i];

      if(note.midi !== midi) {
        continue;
      }

      //if the player pressed the note by the tie end, we want to mark that as incorrect.
      if(note.isTieEnd) {
        continue;
      }

      const delta = Math.abs(note.startRelativeToSpeedAndSeekOffsetMs - playStartTimeMs);

      if(delta < incorrectThresholdMs && delta < closestMatchDelta) {
        closestMatchDelta = delta;
        closestMatchIndex = i;
      }
    }
    return closestMatchIndex !== undefined ? unscoredNotesCriteriaInSelection[closestMatchIndex] : undefined;
  }

  function findMatchingPlayedNoteThatIsClosestInTimeAndThatDoesntExceedTheIncorrectThreshold(
    midi: number,
    startRelativeToSpeedAndSeekOffsetMs: number,
    playerNotes: IPlayerNote[],
    incorrectThresholdMs: number,
    canGetCreditForNoteInWrongOctave: boolean,
    measuresInSegment: Measure[],
    speed: number,
  ) {
    if(playerNotes.length <= 0) {
      return;
    }

    //assume the first note is the closest match.
    let closestMatchIndex = undefined;
    let closestMatchDelta = Number.MAX_SAFE_INTEGER;

    //iterate over the notes and find the nearest played note (in time) that matches the midi number
    for(let i = 0; i < playerNotes.length; ++i) {
      const note = playerNotes[i];

      if(note.midi !== midi) {
        continue;
      }

      const delta = Math.abs(startRelativeToSpeedAndSeekOffsetMs - note.playStartTimeMs);

      if(delta < incorrectThresholdMs && delta < closestMatchDelta) {
        closestMatchDelta = delta;
        closestMatchIndex = i;
      }
    }

    if (canGetCreditForNoteInWrongOctave && !closestMatchIndex) {
      // If we can get credit for notes in the wrong octave, check for notes that are one octave above or below the expected note
      const otherMidiNumbers = [midi - 12, midi + 12];
      for (const otherMidiNumber of otherMidiNumbers) {
        const otherClosestMatchIndex = playerNotes.findIndex((note) => {
          return note.midi === otherMidiNumber && Math.abs(startRelativeToSpeedAndSeekOffsetMs - note.playStartTimeMs) < incorrectThresholdMs;
        });

        if (otherClosestMatchIndex !== -1) {
          closestMatchIndex = otherClosestMatchIndex;
          emitLog({
            message: "Credit awarded for note in wrong octave.",
            data: [
              { key: "Expected", value: midi + "/" + midiNumberToNoteName(midi) + " - " + startRelativeToSpeedAndSeekOffsetMs },
              { key: "CreditFor", value: playerNotes[closestMatchIndex].midi + "/" + midiNumberToNoteName(playerNotes[closestMatchIndex].midi) },
            ]
          });

          const measure = getMeasureIndexFromStartTime(startRelativeToSpeedAndSeekOffsetMs, measuresInSegment[0], speed);
          // This may cause "JSON.stringify cannot serialize cyclic structures." Re-enable after resolving that error.
          // emitLog({ message: "Measure details", data: [{ key: "measure", value: JSON.stringify(measure) }] });

          break;
        }
      }
    }

    return closestMatchIndex !== undefined ? playerNotes[closestMatchIndex] : undefined;
  }

  function getMeasureIndexFromStartTime(startTimeMs: number, sampleMeasure: Measure, speed: number): number {
    let durationTotalMs = 0;
    let measureIndex = 0;
    const measureDurationMs = sampleMeasure.durationSeconds * 1000;

    // adjust measureDurationMs to account for speed
    const adjustedMeasureDurationMs = measureDurationMs * speed;

    while (durationTotalMs < startTimeMs) {
      durationTotalMs += adjustedMeasureDurationMs;
      measureIndex++;
    }

    return measureIndex;
  }

  function midiNumberToNoteName(midiNumber: number): string {
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const octave = Math.floor(midiNumber / 12) - 1; // MIDI note 0 is C-1
    const noteIndex = midiNumber % 12;
    const noteName = notes[noteIndex];
    return `${noteName}${octave}`;
  }

  /**
   * When extraneous notes are played, the SS api drawTempNote requires us to provide bar and percentage into bar the temp note should be placed at.
   * In order to do that, we must iterate over each measure and tally its duration until we get to when the note was played.
   * @param playStartTimeMs - player note start time.
   * @param measuresInSegment - array of all the measures in the segment, so we can evaluate the duration of each,
   * and calculate bar and percentageIntoBar the extraneous note should be placed at.
   * @param seekOffsetSeconds - where playback started from
   * @param speed - tempo modifier.
   */
  function calculateBarIndexAndPercentageIntoBar(playStartTimeMs: number, measuresInSegment: Measure[], seekOffsetSeconds: number, speed: number) {
    if(measuresInSegment.length <= 0) {
      emitLog({ message: "calculateBarNumberAndPercentageIntoBar can't be calculated when no measures are present. This should only happen with segments with video." });
      return;
    }

    const barIndex = getBarIndexByPlayStartTime(playStartTimeMs, measuresInSegment, seekOffsetSeconds, speed);
    const percentageIntoBar = calculatePercentageIntoBar(playStartTimeMs, measuresInSegment, seekOffsetSeconds, speed);

    return {barIndex, percentageIntoBar};
  }

  /**
   * This function calculates the bar index (measure number) at a given point in time during the playback of a song.
   *
   * @param {number} playStartTimeMs - The time at which the note was played by the user, in milliseconds.
   * @param {Measure[]} measuresInSegment - An array of Measure objects, where each Measure represents a bar in the music.
   * @param {number} seekOffsetSeconds - The offset from the start of the song where playback began, in seconds.
   * @param {number} speed - The speed at which the song is being played back. This is a multiplier where 1 represents normal speed, 0.5 represents half speed, etc.
   *
   * @returns {number} - The index of the bar in which the note was played. The first bar has an index of 0.
   */
  function getBarIndexByPlayStartTime(playStartTimeMs: number, measuresInSegment: Measure[], seekOffsetSeconds: number, speed: number): number {
    const playStartTimeAdjustedForSpeed = playStartTimeMs * speed;
    const seekOffsetMs = seekOffsetSeconds * 1000;
    const playStartTimeRelativeToSpeedAndSeekOffsetMs = seekOffsetMs + playStartTimeAdjustedForSpeed;
    let totalMeasureDurationInMs= 0;
    let barIndex = 0;

    // for (const measure of measuresInSegment) {
    //   barIndex++;
    //   totalMeasureDurationInMs += (measure.durationSeconds * 1000);
    //
    //   // We want to return the first measure that ends after the playStartTimeRelativeToSpeedAndSeekOffsetMs
    //   if (totalMeasureDurationInMs >= playStartTimeRelativeToSpeedAndSeekOffsetMs) {
    //     break;
    //   }
    // }

    for(; barIndex < measuresInSegment.length; ++barIndex) {
      const measure = measuresInSegment[barIndex];
      totalMeasureDurationInMs += (measure.durationSeconds * 1000);

      if(totalMeasureDurationInMs >= playStartTimeRelativeToSpeedAndSeekOffsetMs) {
        break;
      }
    }

    return barIndex;
  }

  /**
   * This function calculates the percentage into a bar at a given point in time during the playback of a song.
   *
   * @param {number} playStartTimeMs - The time at which the note was played by the user, in milliseconds.
   * @param {Measure[]} measuresInSegment - An array of Measure objects, where each Measure represents a bar in the music.
   * @param {number} seekOffsetSeconds - The offset from the start of the song where playback began, in seconds.
   * @param {number} speed - The speed at which the song is being played back. This is a multiplier where 1 represents normal speed, 0.5 represents half speed, etc.
   *
   * @returns {number} - The percentage into the bar at which the note was played. This is a value between 0 and 1, where 0 represents the start of the bar and 1 represents the end of the bar.
   */
  function calculatePercentageIntoBar(playStartTimeMs: number, measuresInSegment: Measure[], seekOffsetSeconds: number, speed: number): number {
    const playStartTimeRelativeToSpeedAndSeekOffsetMs = (seekOffsetSeconds * 1000) + (playStartTimeMs * speed);
    let durationTotalMs = 0;
    let measureDurationMs = 0;

    for(let barIndex = 0; barIndex < measuresInSegment.length; ++barIndex) {
      const measure = measuresInSegment[barIndex];
      measureDurationMs = measure.durationSeconds * 1000;
      durationTotalMs += (measure.durationSeconds * 1000);

      if(durationTotalMs >= playStartTimeRelativeToSpeedAndSeekOffsetMs) {
        const durationBeforeLastMeasureMs = durationTotalMs - measureDurationMs;
        const remainingMsIntoBar = playStartTimeRelativeToSpeedAndSeekOffsetMs - durationBeforeLastMeasureMs;
        const percentageIntoBar =  remainingMsIntoBar / measureDurationMs;
        return percentageIntoBar;
      }
    }

    return 0;
  }

  /**
   * Iterate over the note criteria and evaluate if we have passed the threshold where they would be considered missed/incorrect.
   * Tie end notes are not evaluated, as that is done through a different means/function.
   * @param unscoredNotesCriteria - array of notes from the segment
   * @param incorrectThresholdMs - window of time allowed before the note is marked incorrect.
   * @param scoringStartTimeMs - performance.now() time of when beginScoring was first called.
   */
  function scoreUnscoredNotesCriteriaThatWerentPlayedInTime(unscoredNotesCriteria: INoteScoreCriteria[], incorrectThresholdMs: number, scoringStartTimeMs: number) {
    const scoredNotes: IScoredNote[] = [];

    unscoredNotesCriteria.forEach(unscoredNoteCriteria => {
      if (unscoredNoteCriteria.isTieEnd) {
        return;
      }

      const potentialScoredNote = scoreNoteCriteriaThatWasntPlayedInTime(unscoredNoteCriteria, incorrectThresholdMs, scoringStartTimeMs);

      if(potentialScoredNote !== undefined) {
        scoredNotes.push(potentialScoredNote);
      }
    });

    return scoredNotes;
  }

  /**
   * Determine if we have exceeded the window of time where the note is determined to be marked incorrect.
   * @param unscoredNoteCriteria - array of notes from the segment
   * @param incorrectThresholdMs - window of time allowed before the note is marked incorrect.
   * @param scoringStartTimeMs - performance.now() time of when beginScoring was first called.
   */
  function scoreNoteCriteriaThatWasntPlayedInTime(unscoredNoteCriteria: INoteScoreCriteria, incorrectThresholdMs: number, scoringStartTimeMs: number): IScoredNote | undefined{
    const timeSinceScoringStarted = performance.now() - scoringStartTimeMs;
    const delta = timeSinceScoringStarted - unscoredNoteCriteria.startRelativeToSpeedAndSeekOffsetMs;

    if(delta > incorrectThresholdMs) {
      return {
        noteId: unscoredNoteCriteria.noteId,
        playerNoteId: undefined,
        score: NoteEvaluationEnum.incorrect,
        noteScoreCriteria: unscoredNoteCriteria,
        noteScoreReason: NoteScoreReason.IncorrectBecauseItWasNotPlayed,
        barIndex: unscoredNoteCriteria.measureIndex
      };
    }
  }

  /**
   * Calculate the time windows where a note can be played and considered correct or incorrect.
   * https://playgroundsessions.atlassian.net/wiki/spaces/DEV/pages/312803333/Note+Evaluation#Correctness-Thresholds
   * e.g. if the returned correctThresholdMs is 200, and the note is supposed to be played at the 1000 ms mark, the player could play the note between 800 ms and 1200 ms, the note would be marked correct.
   * e.g. if the returned incorrectThresholdMs is 250, and the note is supposed to be played at the 1000 ms mark, if the player played it before 750 ms or after 1250 ms, the note would be marked incorrect.
   * @param speedAdjustedBpm - _current_ tempo of the piece
   * @param lessonDifficulty - difficulty of the lesson. 1-8
   * @param correctThresholdFloorMs - the minimum amount the correct threshold can be.  Useful for pieces with fast tempos.
   * @param correctBeatDivision - modifier used in determining the correct threshold ms
   * @param incorrectThresholdFloorMs - the minimum amount the incorrect threshold can be.  Useful for pieces with fast tempos.
   * @param incorrectBeatDivision - modifier used in determining the absent
   */
  function calculateCorrectAndIncorrectThresholds(
    speedAdjustedBpm: number,
    lessonDifficulty: number,
    correctThresholdFloorMs: number,
    correctBeatDivision: number,
    incorrectThresholdFloorMs: number,
    incorrectBeatDivision: number,
  ) {
    const oneMinuteMs = 60000;

    const singleBeatDuration = oneMinuteMs / speedAdjustedBpm;

    //e.g. for bpm: 81 and difficulty: 8, (60000 / 81) * ((100 - ((8 - 1) * 2.25)) / 100) = 624.0740740740741
    const adjustedSingleBeatDuration = singleBeatDuration * ((100 - ((lessonDifficulty - 1) * 2.25)) / 100);

    //e.g. 624.0740740740741 * .29 = 180.9814814814815
    const calculatedCorrectThreshold = adjustedSingleBeatDuration * correctBeatDivision;

    //e.g. 624.0740740740741 * .38 = 237.14814814814818
    const calculatedIncorrectThreshold = adjustedSingleBeatDuration * incorrectBeatDivision;

    const correctThreshold = Math.max(correctThresholdFloorMs, calculatedCorrectThreshold);
    const incorrectThreshold = Math.max(incorrectThresholdFloorMs, calculatedIncorrectThreshold);
    emitLog({
      message: "Calculated correct and incorrect thresholds",
      data: [
        { key: "correctThreshold", value: correctThreshold },
        { key: "incorrectThreshold", value: incorrectThreshold },
        { key: "speedAdjustedBpm", value: speedAdjustedBpm },
        { key: "lessonDifficulty", value: lessonDifficulty },
        { key: "correctThresholdFloorMs", value: correctThresholdFloorMs },
        { key: "correctBeatDivision", value: correctBeatDivision },
        { key: "incorrectThresholdFloorMs", value: incorrectThresholdFloorMs },
        { key: "incorrectBeatDivision", value: incorrectBeatDivision }
      ]
    });

    return {correctThreshold, incorrectThreshold};
  }

  /**
   * It is useful to do some calculations up front, and only expose the data about a note that we need in order to score a note.
   * This function iterates over the notes from the segment and creates a INoteScoreCriteria for each.
   * @param notes - array of notes to create INoteScoreCriteria for.
   * @param speed - tempo modifier
   * @param seekOffsetSeconds - where playback began
   * @param measuresInSegment - we need to know the measure's duration so we can determine the note type (whole, half, etc)
   */
  function createNotesScoreCriteria(notes: Note[], speed: number, seekOffsetSeconds: number, measuresInSegment: Measure[],): INoteScoreCriteria[] {
    const previousTieNotes: INoteScoreCriteria[] = []; //when there are tie notes, keep track of previous note score criteria so we can assign the previous note to the current note, so we can score by evaluating whether tie is pressed, and whether previous note was played correctly.

    return notes.map(({midi, offset, id, tieEnd, duration, path, tieEndNote, previousTieNote})=>{
      const startRelativeToSpeedMs = offset / speed * 1000;
      const startRelativeToSpeedAndSeekOffsetMs = startRelativeToSpeedMs - (seekOffsetSeconds / speed * 1000);
      const noteId = id || "";
      const {measureIndex, measureDurationSeconds} = findMeasureIndexAndDurationSecondsForNote(path, measuresInSegment);
      const noteType = determineNoteType(duration, measureDurationSeconds);
      const noteScoreCriteria: INoteScoreCriteria = {noteId, midi, startRelativeToSpeedAndSeekOffsetMs, isTieEnd: tieEnd, measureIndex, noteType};

      //if 4 notes are tied together, note 1 will tieEnd false, notes 2,3,4 will tieEnd true. if tieEndNote is true, then it's possible it's the beginning note.
      if(tieEnd || tieEndNote) {
        previousTieNotes.push(noteScoreCriteria);
      }
      //if the current note has a previous tie note (assigned in soundsliceFactory), find the previous note's score criteria so we have a reference to it when scoring, and can evaluate its score.
      if(previousTieNote) {
        const previousTieNoteScoreCriteria = previousTieNotes.find(t => t.noteId === previousTieNote.id);
        if(previousTieNoteScoreCriteria) {
          noteScoreCriteria.previousTieNote = previousTieNoteScoreCriteria;
        } else {
          emitLog({ message: "Can't find previous tie note score criteria, which should never happen.  If it does happen, tie note scoring is probably broken." });
        }
      }
      return noteScoreCriteria;
    });
  }

  /**
   * Give the note's path and array of measures in the segment, find the measure index and duration of the measure, which is useful for determining the note type.
   * @param notePath - contains the bar/measure index
   * @param measuresInSegment - array of measures in the segment
   */
  function findMeasureIndexAndDurationSecondsForNote(notePath: NotePath, measuresInSegment: Measure[]) {
    const measureIndex = notePath.barIndex;
    const measure = measuresInSegment[measureIndex];
    if(!measure) {
      emitLog({ message: "Measure could not be found for note" });
      return {measureIndex: notePath.barIndex, measureDurationSeconds: 1};
    }
    return {measureIndex, measureDurationSeconds: measure.durationSeconds};
  }

  /**
   * Given the measure's duration and the note's duration, determine what type of note it is, which is used for scoring.
   * @param noteDurationSeconds - duration of the note in seconds
   * @param measureDurationSeconds - duration of the measure in seconds
   */
  function determineNoteType(noteDurationSeconds: number, measureDurationSeconds: number) {
    const noteDivision = Math.round(measureDurationSeconds / noteDurationSeconds);
    if(noteDivision > 4) { //eighth == 8
      return NoteTypeEnum.lessThanQuarter;
    } else if (noteDivision > 2 && noteDivision <= 4) { //quarter == 4
      return NoteTypeEnum.quarter;
    } else if (noteDivision > 1 && noteDivision <= 2) {//half == 2
      return NoteTypeEnum.half;
    } else {
      return NoteTypeEnum.whole;//whole == 1
    }
  }

  function emitLog(payload: ILogArguments) {
    self.postMessage({method: 'noteScorerWorkerLog', payload});
  }

  //return function so unit tests can test them.
  return {
    determineNoteType,
    findMeasureIndexAndDurationSecondsForNote,
    createNotesScoreCriteria,
    calculateCorrectAndIncorrectThresholds,
    scoreNoteCriteriaThatWasntPlayedInTime,
    scoreUnscoredNotesCriteriaThatWerentPlayedInTime,
    findMatchingMidiNoteThatIsClosestInTimeAndThatDoesntExceedTheIncorrectThreshold: findMatchingExpectedNoteThatIsClosestInTimeAndThatDoesntExceedTheIncorrectThreshold,
    scorePlayerNote,
    scoreUnscoredTieEndNotes,
  };
}